import type { ManifestFormValues } from "../types";

/**
 * How much of the manifest is filled in, for a progress readout.
 *
 * Counts what is *filled*, not what is "confirmed". The form has no per-field
 * confirm action, so a meter labelled confirmed would either read zero for a
 * clean extraction nobody needed to touch, or count edits and call them
 * confirmations. Filled is what this screen actually moves: on a poor scan most
 * of these are empty and working down the count is the job.
 */

/** Manifest-level fields a complete manifest carries. */
const COUNTED_MANIFEST_FIELDS: readonly (keyof ManifestFormValues)[] = [
  "manifestTrackingNumber",
  "shippedDate",
  "generatorId",
  "generatorName",
  "generatorMailStreet1",
  "generatorMailCity",
  "generatorMailState",
  "generatorMailZip",
  "desFacilityId",
  "desFacilityName",
  "desFacLocationStreet1",
  "desFacLocationCity",
  "desFacLocationState",
  "desFacLocationZip",
];

/**
 * Waste-line fields a line carries whatever it holds.
 *
 * The DOT description and the non-haz description are deliberately absent: a line
 * has one or the other depending on whether it is hazardous, so counting both
 * would put every manifest permanently short of complete.
 */
const COUNTED_WASTE_LINE_FIELDS = [
  "containerNumber",
  "containerTypeCode",
  "quantity",
  "qtyUnitOfMeasureCode",
] as const;

const COUNTED_TRANSPORTER_FIELDS = ["transporterId", "transporterName"] as const;

/**
 * Fields left out on purpose, and why, so the count can reach complete:
 * - Box 18 narratives are blank on a manifest with no discrepancy.
 * - Second address lines are optional.
 * - The TX registration number is Texas-only.
 * - The received date is the receiving facility's, often absent on shipping.
 * - The generator site address is frequently "same as mailing" and left blank.
 */

function isFilled(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (typeof value === "boolean") return value;
  return true;
}

export interface FillProgress {
  filled: number;
  total: number;
}

export function manifestFillProgress(values: ManifestFormValues): FillProgress {
  let filled = 0;
  let total = 0;

  for (const field of COUNTED_MANIFEST_FIELDS) {
    total += 1;
    if (isFilled(values[field])) filled += 1;
  }

  for (const line of values.wasteLines ?? []) {
    // A line's description counts once, whichever of the two it uses.
    total += 1;
    if (isFilled(line.dotPrintedInformation) || isFilled(line.nonHazWasteDescription)) filled += 1;

    for (const field of COUNTED_WASTE_LINE_FIELDS) {
      total += 1;
      if (isFilled(line[field])) filled += 1;
    }
  }

  for (const transporter of values.transporters ?? []) {
    for (const field of COUNTED_TRANSPORTER_FIELDS) {
      total += 1;
      if (isFilled(transporter[field])) filled += 1;
    }
  }

  return { filled, total };
}
